import { useEffect, useState } from 'react';
import {
  api,
  BladApi,
  type CzynnoscZlecenia,
  type KartaZlecenia,
  type Osoba,
  type PozycjaSlownika,
  type Sesja,
} from '../api';
import { data, dataICzas } from '../format';
import { idz } from '../router';
import { FormularzProtokolu } from '../components/FormularzProtokolu';
import { IkonaWstecz } from '../components/Icons';
import { NaglowekWidoku } from '../components/Shell';
import {
  ChipZlecenia,
  Dana,
  Karta,
  Komunikat,
  PoleTekstowe,
  Przycisk,
  PrzyciskMarki,
  Tag,
} from '../components/ui';

const TRYBY: Record<string, string> = { WYJAZD: 'wyjazd do klienta', WARSZTAT: 'warsztat' };
const KANALY: Record<string, string> = {
  QR: 'kod QR na maszynie',
  PORTAL: 'portal klienta',
  KONSULTANT: 'konsultant / telefon',
  SERWISANT: 'serwisant',
};
const RODZAJE: Record<string, string> = {
  GWARANCYJNA: 'Naprawa gwarancyjna',
  POGWARANCYJNA: 'Naprawa pogwarancyjna',
  NIEOBJETA_GWARANCJA: 'Naprawa nieobjęta gwarancją',
  REGULACJA: 'Regulacja',
};

/**
 * Karta zlecenia serwisowego.
 *
 * Przyciski pochodzą **wprost z maszyny stanów** — interfejs ich nie wymyśla i nie
 * decyduje, który jest aktywny. Gdy któryś jest zablokowany, pokazuje powód zamiast
 * po prostu zniknąć: „Zlecenie zamyka podpisany protokół serwisowy".
 *
 * Nigdzie nie ma listy „zmień status" (CLAUDE.md §1 zasada 3).
 */
export function TicketView({ id, sesja }: { id: string; sesja: Sesja }) {
  const mozeProtokol = sesja.permissions.includes('report.write');
  const mozePrzypisac = sesja.permissions.includes('ticket.assign');

  const [karta, setKarta] = useState<KartaZlecenia | null>(null);
  const [slowniki, setSlowniki] = useState<{ objawy: PozycjaSlownika[]; czynnosci: PozycjaSlownika[] } | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [bladAkcji, setBladAkcji] = useState<string | null>(null);
  const [zajete, setZajete] = useState<string | null>(null);
  const [protokolowanie, setProtokolowanie] = useState(false);
  const [pytanieOPowod, setPytanieOPowod] = useState<CzynnoscZlecenia | null>(null);

  useEffect(() => {
    let aktualne = true;
    setKarta(null);
    setProtokolowanie(false);

    api
      .zlecenie(id)
      .then((wynik) => aktualne && setKarta(wynik))
      .catch((error: unknown) => {
        if (aktualne) {
          setBlad(error instanceof BladApi ? error.message : 'Nie udało się wczytać zlecenia.');
        }
      });

    api
      .slownikiSerwisu()
      .then((wynik) => aktualne && setSlowniki(wynik))
      .catch(() => undefined);

    return () => {
      aktualne = false;
    };
  }, [id]);

  async function wykonaj(czynnosc: CzynnoscZlecenia, powod?: string): Promise<void> {
    if (czynnosc.wymagaPowodu && !powod?.trim()) {
      setPytanieOPowod(czynnosc);
      return;
    }

    setZajete(czynnosc.akcja);
    setBladAkcji(null);
    try {
      setKarta(await api.czynnoscZlecenia(id, czynnosc.akcja, powod));
      setPytanieOPowod(null);
    } catch (error: unknown) {
      setBladAkcji(error instanceof BladApi ? error.message : 'Nie udało się zapisać.');
    } finally {
      setZajete(null);
    }
  }

  if (blad) {
    return (
      <>
        <NaglowekWidoku tytul="Zlecenie serwisowe" />
        <Komunikat tresc={blad} />
        <div className="mt-14">
          <Przycisk type="button" onClick={() => idz('/zlecenia')} ikona={<IkonaWstecz rozmiar={16} />}>
            Wróć do listy
          </Przycisk>
        </div>
      </>
    );
  }

  if (!karta) {
    return (
      <>
        <NaglowekWidoku tytul="Zlecenie serwisowe" />
        <p className="t-small text-n-500">Wczytywanie…</p>
      </>
    );
  }

  const { zlecenie, czynnosci, fakty } = karta;
  const nazwaObjawu = (kod: string) => slowniki?.objawy.find((o) => o.kod === kod)?.nazwa ?? kod;
  const nazwaCzynnosci = (kod: string) =>
    slowniki?.czynnosci.find((c) => c.kod === kod)?.nazwa ?? kod;

  // Jedna akcja główna w widoku (CLAUDE.md §5). W toku najważniejsze jest wypełnienie
  // protokołu — to on zamyka zlecenie; poza tym pierwsza dostępna czynność.
  const protokolTeraz = mozeProtokol && zlecenie.status === 'W_TOKU' && !protokolowanie;
  const glowna = protokolTeraz ? null : czynnosci.find((c) => c.dostepna && c.postep);

  return (
    <>
      <NaglowekWidoku
        tytul={`Zlecenie ${zlecenie.numer}`}
        podtytul={`${zlecenie.customer.nazwa} · ${zlecenie.device.producent} ${zlecenie.device.model}`}
        akcje={
          <>
            <Przycisk
              type="button"
              onClick={() => idz('/zlecenia')}
              ikona={<IkonaWstecz rozmiar={16} />}
            >
              Lista
            </Przycisk>
            {protokolTeraz && (
              <PrzyciskMarki type="button" onClick={() => setProtokolowanie(true)}>
                Wypełnij protokół
              </PrzyciskMarki>
            )}
            {glowna && (
              <PrzyciskMarki
                type="button"
                disabled={zajete !== null}
                onClick={() => void wykonaj(glowna)}
              >
                {zajete === glowna.akcja ? 'Zapisywanie…' : glowna.nazwa}
              </PrzyciskMarki>
            )}
          </>
        }
      />

      {bladAkcji && (
        <div className="mb-14">
          <Komunikat tresc={bladAkcji} />
        </div>
      )}

      {pytanieOPowod && (
        <PytanieOPowod
          czynnosc={pytanieOPowod}
          zajete={zajete !== null}
          onPotwierdz={(powod) => void wykonaj(pytanieOPowod, powod)}
          onAnuluj={() => setPytanieOPowod(null)}
        />
      )}

      {protokolowanie && (
        <FormularzProtokolu
          zlecenie={zlecenie}
          czynnosciSlownik={slowniki?.czynnosci ?? []}
          onZapisano={(nowa) => {
            setKarta(nowa);
            setProtokolowanie(false);
          }}
          onAnuluj={() => setProtokolowanie(false)}
        />
      )}

      <div className="grid gap-14 xl:grid-cols-[1fr_340px]">
        <div className="min-w-0">
          <Karta
            tytul="Zgłoszenie"
            akcje={
              <>
                <ChipZlecenia status={zlecenie.status} />
                <Tag>{TRYBY[zlecenie.tryb]}</Tag>
              </>
            }
          >
            <div className="mb-13">
              <span className="t-label block text-n-600">
                Opis zgłoszonej usterki
                <span className="t-small text-n-500"> — wg informacji od klienta</span>
              </span>
              <p className="t-body mt-5 text-n-900">{zlecenie.opisUsterki}</p>
            </div>

            {zlecenie.objawy.length > 0 && (
              <div className="mb-13">
                <span className="t-label mb-5 block text-n-600">Objawy</span>
                <div className="flex flex-wrap gap-5">
                  {zlecenie.objawy.map((kod) => (
                    <Tag key={kod}>{nazwaObjawu(kod)}</Tag>
                  ))}
                </div>
              </div>
            )}

            <dl className="grid grid-cols-[auto_1fr] gap-x-22 gap-y-10">
              <Dana etykieta="Zgłoszono">{data(zlecenie.dataZgloszenia)}</Dana>
              <Dana etykieta="Kanał">{KANALY[zlecenie.kanal]}</Dana>
              <Dana etykieta="Przyjął">{zlecenie.zglaszajacy?.imieNazwisko ?? ''}</Dana>
              <Dana etykieta="Priorytet">{zlecenie.priorytet}</Dana>
              {zlecenie.tryb === 'WARSZTAT' && (
                <>
                  <Dana etykieta="Przyjęte wyposażenie">{zlecenie.wyposazenie ?? ''}</Dana>
                  <Dana etykieta="Lokalizacja w warsztacie">{zlecenie.polka ?? ''}</Dana>
                </>
              )}
            </dl>

            {zlecenie.wstrzymaniePowod && (
              <div className="mt-13">
                <Komunikat
                  rodzaj="info"
                  tresc={`${zlecenie.status === 'ODRZUCONE' ? 'Odrzucone' : 'Wstrzymane'} — ${zlecenie.wstrzymaniePowod}`}
                />
              </div>
            )}
          </Karta>

          <Karta tytul={`Protokoły · ${zlecenie.protokoly.length}`}>
            {zlecenie.protokoly.length === 0 ? (
              <p className="t-small text-n-600">
                Brak protokołu. To on zamyka zlecenie — do tego czasu robota trwa.
              </p>
            ) : (
              zlecenie.protokoly.map((protokol) => (
                <div key={protokol.id} className="mb-14 border-b border-n-100 pb-14 last:mb-0 last:border-b-0 last:pb-0">
                  <div className="mb-9 flex flex-wrap items-center gap-9">
                    <span className="t-body-strong text-n-900">
                      {RODZAJE[protokol.rodzajNaprawy]}
                    </span>
                    <Tag>{String(protokol.czasNaprawyH)} h</Tag>
                    <span className="t-small text-n-600">
                      {protokol.technician.imieNazwisko} · {dataICzas(protokol.wykonaneAt)}
                    </span>
                  </div>

                  <div className="mb-9 flex flex-wrap gap-5">
                    {protokol.czynnosci.map((kod) => (
                      <Tag key={kod}>{nazwaCzynnosci(kod)}</Tag>
                    ))}
                  </div>

                  {protokol.opis && <p className="t-body mb-9 text-n-900">{protokol.opis}</p>}
                  {protokol.zalecenia && (
                    <p className="t-small mb-9 text-n-700">Zalecenia: {protokol.zalecenia}</p>
                  )}

                  {protokol.czesci.length > 0 && (
                    <div className="mb-9 overflow-x-auto">
                      <table className="tab">
                        <thead>
                          <tr>
                            <th>Część</th>
                            <th>Ilość</th>
                            <th>Cena</th>
                          </tr>
                        </thead>
                        <tbody>
                          {protokol.czesci.map((pozycja) => (
                            <tr key={pozycja.id}>
                              <td className="t-body text-n-900">
                                {pozycja.opis}
                                {pozycja.numerCzesci && (
                                  <span className="t-mono-data text-n-500"> {pozycja.numerCzesci}</span>
                                )}
                              </td>
                              <td className="t-mono-data text-n-700">{String(pozycja.ilosc)}</td>
                              <td className="t-mono-data text-n-700">{String(pozycja.cena)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-13 border-t border-n-100 pt-9">
                    <img
                      src={protokol.podpisKlienta.image}
                      alt={`Podpis: ${protokol.podpisKlienta.kto}`}
                      className="h-[54px] border border-n-100 bg-surface"
                    />
                    <div>
                      <div className="t-body-strong text-n-900">{protokol.podpisKlienta.kto}</div>
                      <div className="t-small text-n-600">
                        {dataICzas(protokol.podpisKlienta.kiedy)} · {protokol.podpisKlienta.urzadzenie}
                      </div>
                      {/* Skrót treści to jedyne, co przy sporze pokazuje, pod czym
                          klient faktycznie się podpisał. */}
                      <div className="t-mono-data text-n-500">
                        skrót {protokol.podpisKlienta.hashTresci.slice(0, 16)}…
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </Karta>
        </div>

        <div className="min-w-0">
          {/*
            Przy otwartym protokole panel czynności znika. Zlecenie zamyka podpis, a nie
            przycisk obok — dwa miejsca do klikania w tej samej sprawie to dokładnie ta
            pomyłka, przed którą chroni zasada 3 (status jest skutkiem pracy). Na telefonie
            przy okazji skraca stronę o ekran, którego serwisant i tak by nie użył.
          */}
          {!protokolowanie && (
            <Karta tytul="Co teraz">
              {czynnosci.length === 0 ? (
                <p className="t-small text-n-600">
                  Zlecenie jest zamknięte — nic więcej nie da się z nim zrobić.
                </p>
              ) : (
                <div className="flex flex-col gap-9">
                  {czynnosci.map((czynnosc) => (
                    <div key={czynnosc.akcja}>
                      <Przycisk
                        type="button"
                        disabled={!czynnosc.dostepna || zajete !== null}
                        onClick={() => void wykonaj(czynnosc)}
                      >
                        {zajete === czynnosc.akcja ? 'Zapisywanie…' : czynnosc.nazwa}
                      </Przycisk>
                      {/* Nieaktywny przycisk tłumaczy, czego brakuje — inaczej byłby zagadką. */}
                      {czynnosc.przeszkoda && (
                        <p className="t-small mt-4 text-n-500">{czynnosc.przeszkoda}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Karta>
          )}

          <Przypisanie
            karta={karta}
            mozeEdytowac={mozePrzypisac}
            onZmiana={setKarta}
          />

          <Karta tytul="Maszyna">
            <div className="t-body-strong text-n-900">
              {zlecenie.device.producent} {zlecenie.device.model}
            </div>
            <div className="t-mono-data mb-13 text-n-600">
              nr boczny {zlecenie.device.sideNumber}
            </div>
            <Przycisk type="button" onClick={() => idz(`/urzadzenia/${zlecenie.device.id}`)}>
              Otwórz kartę urządzenia
            </Przycisk>
          </Karta>

          {(fakty.wymagaKolejnejWizyty || fakty.doRozliczenia) && (
            <Karta tytul="Fakty">
              <ul className="m-0 list-none p-0">
                {fakty.wymagaKolejnejWizyty && (
                  <li className="t-body py-4 text-n-900">Wymaga kolejnej wizyty</li>
                )}
                {fakty.doRozliczenia && <li className="t-body py-4 text-n-900">Do rozliczenia</li>}
              </ul>
              <p className="t-small mt-9 text-n-500">
                To nie są statusy — liczą się z danych i nikt ich nie ustawia.
              </p>
            </Karta>
          )}
        </div>
      </div>
    </>
  );
}

/** Czynności, które bez powodu zostawiłyby w bazie zapis nie do odczytania. */
function PytanieOPowod({
  czynnosc,
  zajete,
  onPotwierdz,
  onAnuluj,
}: {
  czynnosc: CzynnoscZlecenia;
  zajete: boolean;
  onPotwierdz: (powod: string) => void;
  onAnuluj: () => void;
}) {
  const [powod, setPowod] = useState('');

  return (
    <form
      onSubmit={(zdarzenie) => {
        zdarzenie.preventDefault();
        if (powod.trim()) onPotwierdz(powod.trim());
      }}
    >
      <Karta tytul={czynnosc.nazwa} wyroznienie>
        <PoleTekstowe
          etykieta="Powód"
          podpowiedz="np. czekamy na zawór — dostawa 20.08"
          value={powod}
          onChange={(z) => setPowod(z.target.value)}
          autoFocus
          autoComplete="off"
        />
        <div className="mt-14 flex flex-wrap items-center gap-13">
          <PrzyciskMarki type="submit" disabled={!powod.trim() || zajete}>
            {zajete ? 'Zapisywanie…' : czynnosc.nazwa}
          </PrzyciskMarki>
          <Przycisk type="button" onClick={onAnuluj} disabled={zajete}>
            Anuluj
          </Przycisk>
          <span className="t-small text-n-500">
            Bez powodu za miesiąc nikt nie odtworzy, co się z tym zleceniem działo.
          </span>
        </div>
      </Karta>
    </form>
  );
}

/**
 * Serwisant i termin — dwa pola, nie kreator planowania. „Zaplanowane" wynika
 * z tego, że oba są ustawione (docs/model-danych.md §5).
 */
function Przypisanie({
  karta,
  mozeEdytowac,
  onZmiana,
}: {
  karta: KartaZlecenia;
  mozeEdytowac: boolean;
  onZmiana: (karta: KartaZlecenia) => void;
}) {
  const { zlecenie, fakty } = karta;
  const [serwisanci, setSerwisanci] = useState<Osoba[] | null>(null);
  const [zapis, setZapis] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);

  useEffect(() => {
    if (!mozeEdytowac) return;
    api
      .serwisanci()
      .then((wynik) => setSerwisanci(wynik.serwisanci))
      .catch(() => setSerwisanci([]));
  }, [mozeEdytowac]);

  async function zapisz(zmiana: { technicianId?: string | null; termin?: string | null }) {
    setZapis(true);
    setBlad(null);
    try {
      onZmiana(await api.przypiszZlecenie(zlecenie.id, zmiana));
    } catch (error: unknown) {
      setBlad(error instanceof BladApi ? error.message : 'Nie udało się zapisać.');
    } finally {
      setZapis(false);
    }
  }

  return (
    <Karta tytul={fakty.zaplanowane ? 'Zaplanowane' : 'Przypisanie i termin'}>
      {!mozeEdytowac ? (
        <dl className="grid grid-cols-[auto_1fr] gap-x-22 gap-y-10">
          <Dana etykieta="Serwisant">{zlecenie.technician?.imieNazwisko ?? ''}</Dana>
          <Dana etykieta="Termin">{data(zlecenie.termin)}</Dana>
        </dl>
      ) : (
        <div className="flex flex-col gap-13">
          <label className="block">
            <span className="t-label mb-5 block text-n-700">Serwisant</span>
            <select
              value={zlecenie.technicianId ?? ''}
              disabled={zapis}
              onChange={(z) => void zapisz({ technicianId: z.target.value || null })}
              className="w-full border border-n-300 bg-surface px-13 text-n-900"
            >
              <option value="">— bez przypisania —</option>
              {serwisanci?.map((osoba) => (
                <option key={osoba.id} value={osoba.id}>
                  {osoba.imieNazwisko}
                </option>
              ))}
            </select>
          </label>

          <PoleTekstowe
            type="date"
            etykieta="Termin"
            value={zlecenie.termin ? zlecenie.termin.slice(0, 10) : ''}
            disabled={zapis}
            onChange={(z) => void zapisz({ termin: z.target.value || null })}
          />
        </div>
      )}

      {blad && (
        <div className="mt-13">
          <Komunikat tresc={blad} />
        </div>
      )}
    </Karta>
  );
}
